/**
 * Navigation Utilities for Platform Navigation
 *
 * These utilities handle tree building, sorting, URL detection,
 * and localization for the dynamic platform menu.
 */

/**
 * Build a hierarchical navigation tree from a flat array of items
 * Uses parent_uid to establish relationships
 *
 * @param {Array} flatItems - Flat array of navigation items from API
 * @returns {Array} - Hierarchical tree with children nested
 */
export const buildNavigationTree = (flatItems) => {
  if (!Array.isArray(flatItems) || flatItems.length === 0) {
    return [];
  }

  // Create a map for quick lookup
  const itemMap = new Map();
  flatItems.forEach((item) => {
    itemMap.set(item.uid, { ...item, children: [] });
  });

  const tree = [];

  // Build the tree by assigning children to parents
  itemMap.forEach((item) => {
    if (item.parent_uid === null) {
      // Root item (logo)
      tree.push(item);
    } else {
      const parent = itemMap.get(item.parent_uid);
      if (parent) {
        parent.children.push(item);
      }
    }
  });

  // Sort children at each level by order
  const sortChildren = (items) => {
    items.sort((a, b) => (a.order || 0) - (b.order || 0));
    items.forEach((item) => {
      if (item.children && item.children.length > 0) {
        sortChildren(item.children);
      }
    });
  };

  sortChildren(tree);

  return tree;
};

/**
 * Sort items by their order property
 *
 * @param {Array} items - Array of navigation items
 * @returns {Array} - Sorted array (mutates original)
 */
export const sortItemsByOrder = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Replace matter term placeholders in a title string
 *
 * Supported placeholders:
 * - {lowercase_singular} - e.g., "client"
 * - {lowercase_plural} - e.g., "clients"
 * - {capitalized_singular} - e.g., "Client"
 * - {capitalized_plural} - e.g., "Clients"
 * - {uppercase_singular} - e.g., "CLIENT"
 * - {uppercase_plural} - e.g., "CLIENTS"
 *
 * @param {string} title - Title string with placeholders
 * @param {Object} matterTerms - Object with translated matter terms:
 *   - singular: lowercase singular (e.g., "client")
 *   - plural: lowercase plural (e.g., "clients")
 *   - capitalizedSingular: capitalized singular (e.g., "Client")
 *   - capitalizedPlural: capitalized plural (e.g., "Clients")
 *   - uppercaseSingular: uppercase singular (e.g., "CLIENT")
 *   - uppercasePlural: uppercase plural (e.g., "CLIENTS")
 * @returns {string} - Title with placeholders replaced
 */
export const resolveMatterTerms = (title, matterTerms) => {
  if (!title || typeof title !== 'string') {
    return title || '';
  }

  if (!matterTerms) {
    return title;
  }

  const {
    singular = 'client',
    plural = 'clients',
    capitalizedSingular = 'Client',
    capitalizedPlural = 'Clients',
    uppercaseSingular = 'CLIENT',
    uppercasePlural = 'CLIENTS',
  } = matterTerms;

  return title
    .replace(/\{lowercase_singular\}/g, singular)
    .replace(/\{lowercase_plural\}/g, plural)
    .replace(/\{capitalized_singular\}/g, capitalizedSingular)
    .replace(/\{capitalized_plural\}/g, capitalizedPlural)
    .replace(/\{uppercase_singular\}/g, uppercaseSingular)
    .replace(/\{uppercase_plural\}/g, uppercasePlural);
};

/**
 * Check if a URL is external (starts with http:// or https://)
 *
 * @param {string} url - URL to check
 * @returns {boolean} - True if external URL
 */
export const isExternalUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Get localized title from a title object
 * Falls back to 'en' if locale not found
 *
 * @param {Object} titleObj - Object with locale keys: { en: "...", he: "..." }
 * @param {string} locale - Current locale (e.g., 'en', 'he')
 * @returns {string} - Localized title or empty string
 */
export const getLocalizedTitle = (titleObj, locale = 'en') => {
  if (!titleObj || typeof titleObj !== 'object') {
    return '';
  }

  // Try requested locale first, then fall back to 'en'
  return titleObj[locale] || titleObj.en || Object.values(titleObj)[0] || '';
};

/**
 * Strip query parameters and hash from a URL for comparison
 * Example: "/app/dashboard?id=123" -> "/app/dashboard"
 *
 * @param {string} url - URL with potential query params
 * @returns {string} - URL without query params and hash
 */
const stripQueryParams = (url) => {
  if (!url) return url;
  return url.split('?')[0].split('#')[0];
};

const getItemNavigationUrl = (item) => item?.url || item?.action?.payload?.path || item?.action?.params?.path || null;

/**
 * Find a navigation item by URL path in the tree
 * Searches recursively through all levels
 * Compares URLs without query parameters
 *
 * @param {Array} tree - Navigation tree
 * @param {string} url - URL to find (may include query params)
 * @returns {Object|null} - Found item or null
 */
export const findItemByUrl = (tree, url) => {
  if (!Array.isArray(tree) || !url) {
    return null;
  }

  // Strip query params from the search URL for comparison
  const normalizedUrl = stripQueryParams(url);

  return tree.reduce((found, item) => {
    if (found) return found;
    
    // Search children first to prioritize deeper matches
    // This ensures that if both parent and child have the same URL,
    // we return the child (more specific match)
    if (item.children && item.children.length > 0) {
      const childMatch = findItemByUrl(item.children, url);
      if (childMatch) return childMatch;
    }
    
    const itemUrl = stripQueryParams(getItemNavigationUrl(item));
    if (itemUrl === normalizedUrl) return item;
    
    return null;
  }, null);
};

/**
 * Find a navigation item by UID in the tree
 * Searches recursively through all levels
 *
 * @param {Array} tree - Navigation tree
 * @param {string} uid - UID to find
 * @returns {Object|null} - Found item or null
 */
export const findItemByUid = (tree, uid) => {
  if (!Array.isArray(tree) || !uid) {
    return null;
  }

  return tree.reduce((found, item) => {
    if (found) return found;
    if (item.uid === uid) return item;
    if (item.children && item.children.length > 0) {
      return findItemByUid(item.children, uid);
    }
    return null;
  }, null);
};

/**
 * Get the path from root to a specific item (for breadcrumbs/active state)
 * Returns array of UIDs from root to the item
 *
 * @param {Array} tree - Navigation tree
 * @param {string} uid - Target item UID
 * @returns {Array} - Array of UIDs from root to item, or empty array if not found
 */
export const getAncestorPath = (tree, uid) => {
  if (!Array.isArray(tree) || !uid) {
    return [];
  }

  const findPath = (items, targetUid, currentPath = []) => items.reduce((foundPath, item) => {
    if (foundPath.length > 0) return foundPath;

    const newPath = [...currentPath, item.uid];

    if (item.uid === targetUid) {
      return newPath;
    }

    if (item.children && item.children.length > 0) {
      return findPath(item.children, targetUid, newPath);
    }

    return [];
  }, []);

  return findPath(tree, uid);
};

/**
 * Get the root item (logo) from the tree
 * The root item has parent_uid === null
 *
 * @param {Array} tree - Navigation tree (already built)
 * @returns {Object|null} - Root item or null
 */
export const getRootItem = (tree) => {
  if (!Array.isArray(tree) || tree.length === 0) {
    return null;
  }

  // In a properly built tree, the root item is at the top level
  // and has parent_uid === null
  return tree.find((item) => item.parent_uid === null) || tree[0] || null;
};

/**
 * Get top-level navigation items (direct children of the logo/root)
 *
 * @param {Array} tree - Navigation tree
 * @returns {Array} - Array of top-level items
 */
export const getTopLevelItems = (tree) => {
  const root = getRootItem(tree);
  if (!root || !root.children) {
    return [];
  }
  return root.children;
};

/**
 * Get children of a specific item by UID
 *
 * @param {Array} tree - Navigation tree
 * @param {string} uid - Parent item UID
 * @returns {Array} - Array of children or empty array
 */
export const getChildrenOf = (tree, uid) => {
  const item = findItemByUid(tree, uid);
  return item?.children || [];
};

/**
 * Check if an item has children
 *
 * @param {Object} item - Navigation item
 * @returns {boolean} - True if item has children
 */
export const hasChildren = (item) => Boolean(item?.children && item.children.length > 0);

/**
 * Find the top-level parent of an item
 * Useful for determining which top bar item should be active
 *
 * @param {Array} tree - Navigation tree
 * @param {string} uid - Item UID to find parent for
 * @returns {Object|null} - Top-level parent item or null
 */
export const findTopLevelParent = (tree, uid) => {
  const path = getAncestorPath(tree, uid);

  if (path.length < 2) {
    // Item is root or not found
    return null;
  }

  // Path[0] is root (logo), path[1] is the top-level item
  return findItemByUid(tree, path[1]);
};

/**
 * Find an item by a property value
 * Recursively searches the tree for an item matching the property
 *
 * @param {Array} tree - Navigation tree
 * @param {string} property - Property name to search for
 * @param {*} value - Value to match
 * @returns {Object|null} - Found item or null
 */
export const findItemByProperty = (tree, property, value) => {
  if (!Array.isArray(tree)) {
    return null;
  }

  return tree.reduce((found, item) => {
    if (found) return found;
    if (item[property] === value) return item;
    if (item.children && item.children.length > 0) {
      return findItemByProperty(item.children, property, value);
    }
    return null;
  }, null);
};

/**
 * Route alias mappings
 * Maps alternative route paths to their canonical navigation item URLs
 * Example: /app/scheduler -> /app/calendar
 */
const ROUTE_ALIASES = {
  '/app/scheduler': '/app/calendar',
};

/**
 * Get the canonical URL for a route path (resolve aliases)
 * Handles both exact matches and paths that start with an alias
 * Example: /app/scheduler -> /app/calendar
 * Example: /app/scheduler/123 -> /app/calendar/123
 *
 * @param {string} path - Route path
 * @returns {string} - Canonical URL (or original if no alias)
 */
export const resolveRouteAlias = (path) => {
  // Try exact match first
  if (ROUTE_ALIASES[path]) {
    return ROUTE_ALIASES[path];
  }

  // Try prefix match (for entity pages with aliases)
  const aliasKeys = Object.keys(ROUTE_ALIASES);
  const matchingAlias = aliasKeys.find((alias) => path.startsWith(`${alias}/`) || path === alias);
  
  if (matchingAlias) {
    const canonicalBase = ROUTE_ALIASES[matchingAlias];
    // Replace the alias prefix with canonical prefix
    return path.replace(matchingAlias, canonicalBase);
  }

  return path;
};

/**
 * Find navigation item by URL prefix (for entity pages)
 * Matches items where the URL is a prefix of the given path
 * Example: /app/appointments/123 matches /app/appointments
 *
 * @param {Array} tree - Navigation tree
 * @param {string} url - URL path to match
 * @returns {Object|null} - Found item or null
 */
export const findItemByUrlPrefix = (tree, url) => {
  if (!Array.isArray(tree) || !url) {
    return null;
  }

  // Strip query params from the search URL for comparison
  const normalizedUrl = stripQueryParams(url);

  return tree.reduce((found, item) => {
    if (found) return found;
    
    const itemUrl = stripQueryParams(getItemNavigationUrl(item));
    
    // Check if item URL is a prefix of the given URL
    if (itemUrl && normalizedUrl.startsWith(itemUrl) && itemUrl !== '/') {
      // For entity pages, prefer child items if they match more closely
      if (item.children && item.children.length > 0) {
        const childMatch = findItemByUrlPrefix(item.children, url);
        if (childMatch) {
          return childMatch;
        }
      }
      return item;
    }
    
    // Recursively search children
    if (item.children && item.children.length > 0) {
      return findItemByUrlPrefix(item.children, url);
    }
    
    return null;
  }, null);
};

/**
 * Find the best matching navigation item for a route path
 * Tries: exact match -> prefix match -> alias match
 *
 * @param {Array} tree - Navigation tree
 * @param {string} path - Route path
 * @returns {Object|null} - Best matching item or null
 */
export const findBestMatchingItem = (tree, path) => {
  if (!path) return null;

  // 1. Try exact match
  let item = findItemByUrl(tree, path);
  if (item) return item;

  // 2. Try prefix match (for entity pages like /app/appointments/123)
  item = findItemByUrlPrefix(tree, path);
  if (item) return item;

  // 3. Try alias match
  const canonicalPath = resolveRouteAlias(path);
  if (canonicalPath !== path) {
    item = findItemByUrl(tree, canonicalPath);
    if (item) return item;
    
    // Also try prefix match with canonical path
    item = findItemByUrlPrefix(tree, canonicalPath);
    if (item) return item;
  }

  return null;
};

export default {
  buildNavigationTree,
  sortItemsByOrder,
  resolveMatterTerms,
  isExternalUrl,
  getLocalizedTitle,
  findItemByUrl,
  findItemByUid,
  getAncestorPath,
  getRootItem,
  getTopLevelItems,
  getChildrenOf,
  hasChildren,
  findTopLevelParent,
  resolveRouteAlias,
  findItemByUrlPrefix,
  findBestMatchingItem,
  findItemByProperty,
};
